import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useFormatPolePrice } from 'components/common/hooks/useFormatPolePrice'
import {
  findChangedPendingRequest,
  findPendingRequest,
} from 'components/station/components/competitor-pricing/utils/helpers'
import { usePriceChange } from 'components/station/components/competitor-pricing/context/priceChangeContext'
import { PricingTypes } from 'utils/constants'
import classNames from 'classnames/bind'
import { CashCreditDifferential } from '../../../cash-credit-differential'
import { CombinedPriceControl } from '../../../combined-price-control'
import { PriceButton } from '../../../price-button'
import styles from './EditPrices.module.scss'
import { ManualInput } from '../../../manual-input'

const cx = classNames.bind(styles)

const { CASH, CREDIT } = PricingTypes

const renderButtons = (
  show,
  dispatch,
  fuelGradeId,
  pricingType,
  setManualInputEnabled,
  pendingRequest
) => {
  if (!show) {
    return null
  }

  return (
    <div className={cx('buttons')}>
      <PriceButton
        setManualInputEnabled={setManualInputEnabled}
        dispatch={dispatch}
        fuelGradeId={fuelGradeId}
        pricingType={pricingType}
        pendingRequest={pendingRequest}
      />
      <PriceButton
        setManualInputEnabled={setManualInputEnabled}
        dispatch={dispatch}
        minus
        fuelGradeId={fuelGradeId}
        pricingType={pricingType}
        pendingRequest={pendingRequest}
      />
    </div>
  )
}

const EditPrices = ({
  stationId,
  fuelGradeId,
  fuelGradeName,
  cashPrice,
  creditPrice,
  fuelGradeDifferential,
  cashCreditDifferential,
  sliderDispatch,
}) => {
  const [{ pendingRequests }, dispatch] = usePriceChange()
  const [manualInputEnabled, setManualInputEnabled] = useState(true)
  const { formatPolePrice } = useFormatPolePrice()
  const inputRefCash = useRef()
  const inputRefCredit = useRef()

  const pendingCashRequest = findChangedPendingRequest(
    pendingRequests,
    CASH,
    fuelGradeId
  )
  const pendingCreditRequest = findChangedPendingRequest(
    pendingRequests,
    CREDIT,
    fuelGradeId
  )

  const spreadSet = Boolean(cashCreditDifferential)
  const gradeAllowsControls =
    !fuelGradeDifferential || fuelGradeDifferential.leadGrade
  // When a cash/credit spread is set the two prices are locked together, so the
  // individual steppers/manual inputs are hidden — only the combined control remains.
  const showPriceButtonsAndManualInput = gradeAllowsControls && !spreadSet
  const showCashPriceButtons = cashPrice && showPriceButtonsAndManualInput
  const showCreditPriceButtons = creditPrice && showPriceButtonsAndManualInput
  // The combined control moves both prices together (keeping the gap), so it shows
  // whenever the grade has both prices — with or without a spread set.
  const showCombinedControl =
    gradeAllowsControls && Boolean(cashPrice) && Boolean(creditPrice)

  const newCashPrice = pendingCashRequest?.newPrice
  const newCreditPrice = pendingCreditRequest?.newPrice

  return (
    <div className={cx('edit-prices')}>
      {showCombinedControl && (
        <CombinedPriceControl
          dispatch={dispatch}
          fuelGradeId={fuelGradeId}
          fuelGradeName={fuelGradeName}
          cashPendingRequest={findPendingRequest(
            pendingRequests,
            CASH,
            fuelGradeId
          )}
          creditPendingRequest={findPendingRequest(
            pendingRequests,
            CREDIT,
            fuelGradeId
          )}
        />
      )}
      <div className={cx('prices-row')}>
        <span
          className={cx('cash-price', {
            centered: !showPriceButtonsAndManualInput,
          })}
        >
        <div className={cx('price-info')}>
          <div className={cx('current-price')}>
            {formatPolePrice(newCashPrice && cashPrice?.price, 'Cash')}
          </div>
          <div className={cx('price')}>
            {showPriceButtonsAndManualInput ? (
              <ManualInput
                newPrice={newCashPrice}
                enabled={manualInputEnabled}
                setEnabled={setManualInputEnabled}
                price={cashPrice?.price}
                fuelGradeId={fuelGradeId}
                pricingType={CASH}
                inputRef={inputRefCash}
                dispatch={dispatch}
              />
            ) : newCashPrice ? (
              formatPolePrice(newCashPrice)
            ) : (
              formatPolePrice(cashPrice?.price)
            )}
          </div>
        </div>
        {renderButtons(
          showCashPriceButtons,
          dispatch,
          fuelGradeId,
          CASH,
          setManualInputEnabled,
          pendingCashRequest
        )}
      </span>
      <span className={cx('divider')} />
      <span
        className={cx('credit-price', {
          centered: !showPriceButtonsAndManualInput,
        })}
      >
        <div className={cx('price-info')}>
          <div className={cx('current-price')}>
            {formatPolePrice(newCreditPrice && creditPrice?.price, 'Credit')}
          </div>
          <div className={cx('price')}>
            {showPriceButtonsAndManualInput ? (
              <ManualInput
                newPrice={newCreditPrice}
                enabled={manualInputEnabled}
                setEnabled={setManualInputEnabled}
                price={creditPrice?.price}
                fuelGradeId={fuelGradeId}
                pricingType={CREDIT}
                inputRef={inputRefCredit}
                dispatch={dispatch}
              />
            ) : newCreditPrice ? (
              formatPolePrice(newCreditPrice)
            ) : (
              formatPolePrice(creditPrice?.price)
            )}
          </div>
        </div>
        {renderButtons(
          showCreditPriceButtons,
          dispatch,
          fuelGradeId,
          CREDIT,
          setManualInputEnabled,
          pendingCreditRequest
        )}
        </span>
      </div>
      <CashCreditDifferential
        stationId={stationId}
        fuelGradeId={fuelGradeId}
        cashCreditDifferential={cashCreditDifferential}
        sliderDispatch={sliderDispatch}
      />
    </div>
  )
}

EditPrices.propTypes = {
  stationId: PropTypes.string.isRequired,
  fuelGradeId: PropTypes.string.isRequired,
  fuelGradeName: PropTypes.string.isRequired,
  cashPrice: PropTypes.shape(),
  creditPrice: PropTypes.shape(),
  fuelGradeDifferential: PropTypes.shape(),
  cashCreditDifferential: PropTypes.shape(),
  sliderDispatch: PropTypes.func.isRequired,
}

EditPrices.defaultProps = {
  cashPrice: null,
  creditPrice: null,
  fuelGradeDifferential: null,
  cashCreditDifferential: null,
}

export { EditPrices }
